import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import CoverageStrip from '../../components/feature/CoverageStrip';
import SiteWidgets from '../../components/feature/SiteWidgets';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

function EVQuoteForm() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!postcode.trim()) { setError('Please enter your postcode'); return; }
    setError('');
    setSubmitting(true);

    // Submit to form endpoint in background
    try {
      const body = new URLSearchParams({ service: 'EV Charger', postcode });
      fetch('https://readdy.ai/api/form/d7426jr41deorl02el20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // Silent fail - still redirect
    }

    // Redirect to solar quote wizard
    const params = new URLSearchParams({ service: 'EV Charger', postcode });
    navigate(`/solar-quote?${params.toString()}`);
  };

  if (submitted) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3">
          <i className="ri-checkbox-circle-fill text-[#7AC943] text-6xl" />
        </div>
        <h3 className="font-bold text-xl text-[#1F2933] mb-2">Taking you to the quote wizard...</h3>
        <p className="text-gray-500 text-sm">Just a moment while we set up your EV charger quote.</p>
      </div>
    );
  }

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <input
        type="text"
        name="postcode"
        value={postcode}
        onChange={(e) => setPostcode(e.target.value.toUpperCase())}
        placeholder="Enter your postcode"
        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm text-[#1F2933] focus:outline-none focus:border-[#4CAF50] transition-colors"
        maxLength={8}
      />
      {error && <p className="text-red-500 text-xs">{error}</p>}
      <button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-[#145A32] to-[#4CAF50] text-white font-bold py-4 rounded-xl text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-70">
        {submitting ? 'Getting your quote...' : 'Get my EV charger quote →'}
      </button>
      <p className="text-xs text-gray-400 text-center">No obligation. Fixed prices. Response within 24 hours.</p>
    </form>
  );
}

const benefits = [
  { icon: 'ri-flashlight-line', title: 'Charge 6x faster than a 3-pin', desc: 'A 7.4kW home charger fills most EVs overnight — far faster than any standard plug socket.' },
  { icon: 'ri-money-pound-circle-line', title: 'Save up to £1,200 per year', desc: 'Charge at home on cheap overnight tariffs like Octopus Go — a fraction of public charger costs.' },
  { icon: 'ri-smartphone-line', title: 'Smart app scheduling', desc: 'Schedule charging to run at the cheapest times automatically from your phone or smart tariff.' },
  { icon: 'ri-sun-line', title: 'Solar-powered charging', desc: 'Pair with your solar panels to charge your EV from free, clean energy you generate yourself.' },
  { icon: 'ri-award-line', title: 'OZEV approved installers', desc: 'All our chargers are installed by OZEV-authorised engineers — required for government grant eligibility.' },
  { icon: 'ri-tools-line', title: '3-year charger warranty', desc: 'Every charger comes with a minimum 3-year warranty, and our installation is guaranteed for life.' },
];

const chargers = [
  {
    brand: 'Ohme Home Pro',
    power: '7.4kW',
    smart: true,
    solar: false,
    price: '£799',
    desc: 'The UK\'s most popular smart EV charger. Works with all major smart tariffs and integrates with your car.',
  },
  {
    brand: 'Zappi V2',
    power: '7.4kW',
    smart: true,
    solar: true,
    price: '£999',
    desc: 'The number one solar-compatible EV charger. Automatically diverts excess solar energy to charge your car.',
    popular: true,
  },
  {
    brand: 'Hypervolt Pro 3',
    power: '7.4kW',
    smart: true,
    solar: false,
    price: '£1,099',
    desc: 'Sleek, ultra-premium smart charger with built-in data monitoring and a 5-year manufacturer warranty.',
  },
];

const faqs = [
  { q: 'Do I need a dedicated circuit for an EV charger?', a: 'Yes — most home EV chargers require a dedicated 32A circuit from your consumer unit. Our engineers assess this during installation.' },
  { q: 'How long does installation take?', a: 'A standard home EV charger installation takes around 3–4 hours and causes minimal disruption.' },
  { q: 'Can I charge my EV using my solar panels?', a: 'Yes! With a compatible charger like the Zappi, you can automatically divert excess solar power directly to your car for free charging.' },
  { q: 'Is there a government grant for home EV chargers?', a: 'The OZEV EV Chargepoint Grant offers up to £350 off the cost of a home charger for eligible property types. Our installers handle the application for you.' },
];

export default function EVChargerPage() {
  const [visible, setVisible] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const benefitsAnim = useScrollAnimation();
  const chargersAnim = useScrollAnimation();
  const faqAnim = useScrollAnimation();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="min-h-screen bg-white font-sans">
      <Header />

      {/* Hero */}
      <section className="bg-[#145A32] pt-24 md:pt-40 pb-0 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className={`flex-1 pb-8 md:pb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <Link to="/" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-6 transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line text-sm" /></div>
                Back to home
              </Link>
              <div className="text-[#7AC943] text-xs font-bold uppercase tracking-widest mb-3">EV CHARGING</div>
              <h1 className="font-heading font-black text-white text-4xl md:text-5xl leading-tight mb-4">
                Commercial &amp; home<br />EV charger installation
              </h1>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-4 max-w-lg">
                OZEV-approved EV charging for homes, businesses, car parks and fleets. Covering London, Essex, Suffolk and Cambridge — installed by certified engineers from just £799.
              </p>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-2-line text-[#7AC943] text-sm" /></div>
                <span className="text-[#7AC943] text-sm font-semibold">London · Essex · Suffolk · Cambridge</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href="#get-quote" className="bg-[#7AC943] hover:bg-[#6ab538] text-white font-bold px-6 py-3.5 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap">
                  Get a free quote
                </a>
                <a href="#chargers" className="bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3.5 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap">
                  View chargers
                </a>
              </div>
              <div className="flex flex-wrap gap-6 mt-8">
                {['OZEV Approved', '7.4kW Fast Charging', 'Smart Scheduling'].map(t => (
                  <div key={t} className="flex items-center gap-2 text-white/80 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-[#7AC943]" /></div>
                    {t}
                  </div>
                ))}
              </div>
            </div>
            <div className={`flex-1 flex items-end justify-center md:justify-end transition-all duration-1000 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <img
                src="https://public.readdy.ai/ai/img_res/ec693fd8638ef3193d28486af4972500.jpg"
                alt="EV charger installation"
                className="w-full max-w-lg h-[300px] md:h-[420px] object-cover object-center rounded-t-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div
            ref={benefitsAnim.ref}
            className={`text-center mb-10 transition-all duration-700 ${benefitsAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">WHY CHARGE AT HOME?</div>
            <h2 className="font-heading font-black text-[#1F2933] text-2xl md:text-3xl">The benefits of home EV charging</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                style={{ transitionDelay: `${i * 80}ms` }}
                className={`bg-[#f7f8fa] rounded-xl p-6 hover:-translate-y-1 transition-all duration-300 ${benefitsAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#145A32]/10 mb-4">
                  <i className={`${b.icon} text-[#145A32] text-xl`} />
                </div>
                <h3 className="font-bold text-[#1F2933] text-base mb-2">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Charger models */}
      <section id="chargers" className="py-14 md:py-20 bg-[#f7f8fa]">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div
            ref={chargersAnim.ref}
            className={`text-center mb-10 transition-all duration-700 ${chargersAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">CHARGERS</div>
            <h2 className="font-heading font-black text-[#1F2933] text-2xl md:text-3xl">Our EV charger range</h2>
            <p className="text-gray-500 text-sm mt-2">All prices are fully installed including VAT.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {chargers.map((c, i) => (
              <div
                key={c.brand}
                style={{ transitionDelay: `${i * 100}ms` }}
                className={`bg-white rounded-2xl border-2 p-6 relative transition-all duration-300 hover:-translate-y-1 ${'popular' in c && c.popular ? 'border-[#4CAF50]' : 'border-gray-100'} ${chargersAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                {'popular' in c && c.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4CAF50] text-white text-[10px] font-bold px-4 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
                    Best for Solar
                  </div>
                )}
                <div className="text-[#145A32] font-black text-lg mb-1">{c.brand}</div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="text-[10px] bg-[#145A32]/10 text-[#145A32] font-bold px-2 py-0.5 rounded-md uppercase">{c.power}</span>
                  {c.smart && <span className="text-[10px] bg-[#4CAF50]/10 text-[#4CAF50] font-bold px-2 py-0.5 rounded-md uppercase">Smart</span>}
                  {c.solar && <span className="text-[10px] bg-[#7AC943]/10 text-[#7AC943] font-bold px-2 py-0.5 rounded-md uppercase">Solar Ready</span>}
                </div>
                <div className="text-[#1F2933] font-bold text-2xl mb-2">{c.price}</div>
                <p className="text-gray-500 text-sm mb-5 leading-relaxed">{c.desc}</p>
                <a href="#get-quote" className="block w-full text-center bg-gradient-to-r from-[#145A32] to-[#4CAF50] text-white font-bold py-3 rounded-xl text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap">
                  Get this charger
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Strip */}
      <CoverageStrip />

      {/* FAQs */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div
            ref={faqAnim.ref}
            className={`text-center mb-10 transition-all duration-700 ${faqAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">FAQ</div>
            <h2 className="font-heading font-black text-[#1F2933] text-2xl md:text-3xl">Frequently asked questions</h2>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                style={{ transitionDelay: `${i * 80}ms` }}
                className={`border border-gray-100 rounded-xl overflow-hidden transition-all duration-500 ${faqAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                <button
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-[#1F2933] text-sm pr-4">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-[#4CAF50] text-lg`} />
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-40' : 'max-h-0'}`}>
                  <p className="px-5 pb-5 text-gray-500 text-sm leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote form */}
      <section id="get-quote" className="py-14 md:py-20 bg-[#145A32]">
        <div className="max-w-2xl mx-auto px-4 md:px-8 text-center">
          <div className="text-[#7AC943] text-xs font-bold uppercase tracking-widest mb-3">GET STARTED</div>
          <h2 className="font-heading font-black text-white text-2xl md:text-3xl mb-3">Get your free EV charger quote</h2>
          <p className="text-white/70 text-sm mb-8 leading-relaxed">Enter your postcode and we&apos;ll match you with an OZEV-approved installer in your area.</p>
          <div className="bg-white rounded-2xl p-6 md:p-8">
            <EVQuoteForm />
          </div>
        </div>
      </section>

      <Footer />
      <SiteWidgets />
    </main>
  );
}
